import React, { useEffect, useMemo, useRef } from 'react'
import { Trans } from 'react-i18next'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'

import { BottomBarType, BuilderPage } from '@makyoapp/makyo-common'

import { IAppPage } from '@/core/app-builder/app/page'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useLoadTableIds } from '@/hooks/__project__/useLoadTableId'
import { usePageTitle } from '@/hooks/__project__/usePageTitle'
import { useBreakpoint } from '@/hooks/useBreakpoint'

import { BrandingButton } from './BrandingButton'
import { BuilderComponentCoreProps } from './BuilderComponentCore'
import { BuilderNavBar } from './BuilderNavBar'
import { BuilderTopBarCore } from './BuilderTopBarCore'
import { CookieConsentComponent } from './CookieConsent'
import { GlobalAppLoading } from './GlobalAppLoading'

const usePageActions = (page: BuilderPage) => {
  const { simulateAction } = useBuilderActionContextV2()

  const alreadyExecutedRef = useRef<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const onLoadActions = page.actions.onLoad
  const pageTitle = usePageTitle(page)

  useEffect(() => {
    if (onLoadActions.length === 0 || alreadyExecutedRef.current) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      alreadyExecutedRef.current = true
      simulateAction({
        actions: onLoadActions,
        triggerSource: {
          id: page.id,
          label: pageTitle,
          type: 'page',
        },
      })
    }, 300)

    // cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
      alreadyExecutedRef.current = false
    }
  }, [onLoadActions, simulateAction, page.id, pageTitle])
}

export interface BuilderPageCoreProps {
  page: BuilderPage
  pageClient: IAppPage
  children: (props: BuilderComponentCoreProps) => React.ReactNode
}

/**
 * Builder Page Core
 *
 * @todo:
 * - [ ] use builderGlobals for rendering
 */
export const BuilderPageCore = React.memo(
  React.forwardRef<HTMLDivElement, BuilderPageCoreProps>((props, ref) => {
    const { dbClient, language, userSpecific, loadedTables, wasmProcessing } = useDbClientContext()
    // access navbar settings and app client
    const { builderGlobals, navbarSettings, topBarVisibility, showCookieConfirmation, appClient } =
      useAppBuilderContext()

    // Destructure props
    const { page, pageClient, children } = props

    // access rowId from url
    const params = useParams()
    const detailRowId = params['rowId'] || ''
    const detailTableId = pageClient.settings._TableID

    // load table ids for the page
    useLoadTableIds(pageClient)

    // attach/detach globals to/from the page client
    useEffect(() => {
      if (!appClient || !pageClient) return

      pageClient.AttachGlobals()

      const unsubscribe = appClient.subscribe((type) => {
        if (type === 'globals') {
          pageClient.AttachGlobals()
        }
      })

      return () => {
        pageClient.DetachGlobals()
        unsubscribe()
      }
    }, [appClient, pageClient])

    // handle page actions
    usePageActions(page)

    const detailTable = detailTableId ? dbClient?.tablesMap.get(detailTableId) : undefined

    const detailRowIndex = useMemo(() => {
      if (!detailTable || !detailRowId || !loadedTables.has(detailTable.id)) return undefined
      return detailTable.GetRowIndexByRowId(detailRowId, language, userSpecific)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailTable, detailRowId, language, userSpecific, loadedTables, wasmProcessing])

    const rowDataNotFound = !!detailTableId && !!detailRowId && detailRowIndex === -1

    // access breakpoint
    const {
      isNavigationBarTop,
      isNavPositionLeading,
      isPhonePortrait,
      isPhone,
      isTabletPortrait,
      isTabletLandscape,
      isDesktop,
      isTV,
      menuHeight,
      isSticky,
      isNavigationBarBottom,
      getMenuHeight,
    } = useBreakpoint()
    const isLargeDevice = isTabletLandscape || isDesktop || isTV
    const isSmallDevice = isPhone || isTabletPortrait

    // get height based on navbar visibility
    const getHeight = useMemo(() => {
      if (page?.bbType !== BottomBarType.Global) return undefined

      if (isNavPositionLeading && topBarVisibility) return `calc(100% - 64px)`
      if (isNavPositionLeading && !topBarVisibility) return undefined

      const { show } = navbarSettings.desktop

      if (topBarVisibility) {
        if (!show) return '100%'
        if (isNavigationBarTop && isPhonePortrait) return `calc(100% + 64px - ${menuHeight})`
        if (isNavigationBarBottom && isLargeDevice && !isSticky) return `calc(100% - 64px)`
        if (!isSticky) return undefined
        return `calc(100% - 64px - ${menuHeight})`
      } else {
        if (!show) return '100%'
        if (isNavigationBarTop && isPhonePortrait) return `calc(100% + 64px + ${menuHeight})`
        if ((isNavigationBarBottom || isNavigationBarTop) && !isSticky) return undefined
        return `calc(100% - ${menuHeight})`
      }
    }, [
      page?.bbType,
      isNavPositionLeading,
      topBarVisibility,
      navbarSettings.desktop,
      isNavigationBarTop,
      isPhonePortrait,
      menuHeight,
      isNavigationBarBottom,
      isLargeDevice,
      isSticky,
    ])

    // show top bar
    const showTopBar =
      (!isSticky && !isNavPositionLeading && isSmallDevice) || (!isSticky && isLargeDevice && isNavigationBarTop)

    // show navbar position on top
    const showTopNavBar = !isSticky && isNavigationBarTop && !isPhonePortrait

    // show navbar position on leading or bottom
    const showBottomNavBar = isSticky || isNavigationBarBottom || isNavPositionLeading

    const pageHasHeader = useMemo(() => page.elements.some((e) => e.compId === 'header'), [page.elements])
    const pageHasFooter = useMemo(() => page.elements.some((e) => e.compId === 'footer'), [page.elements])
    const globalSettings = (pageClient.settings as any)?._GlobalSettings || {}
    const headerVisible = globalSettings._HideHeader !== true
    const footerVisible = globalSettings._HideFooter !== true
    const showGlobalHeader = headerVisible && !pageHasHeader
    const showGlobalFooter = footerVisible && !pageHasFooter

    const globalHeaderElements = useMemo(() => {
      return builderGlobals.elements.filter((e) => e.compId === 'header' && e.groupId === 'globals')
    }, [builderGlobals.elements])

    const globalFooterElements = useMemo(() => {
      return builderGlobals.elements.filter((e) => e.compId === 'footer' && e.groupId === 'globals')
    }, [builderGlobals.elements])

    // handle row data not found in details page
    if (rowDataNotFound) {
      return window.appMode === 'public' ? (
        <div className='p-8'>
          <h1 className='text-lg font-bold'>
            <Trans>Details not visible</Trans>
          </h1>
          <p className='mt-2'>
            <Trans>ROW_DATA_NOT_FOUND_PUBLIC</Trans>
          </p>
        </div>
      ) : (
        <div className='p-8'>
          <Trans>ROW_DATA_NOT_FOUND_BUILDER</Trans>
        </div>
      )
    }

    return (
      <div
        id='page-main'
        ref={ref}
        className={clsx(
          'droppable mk-scroll flex flex-col',
          page.bbType === BottomBarType.Contextual && 'with-bot-bar'
        )}
        style={{
          height: getHeight,
          minHeight: `calc(102vh - ${isSticky && isNavigationBarBottom ? getMenuHeight(menuHeight) : '0px'})`,
          paddingBottom: isSticky && isNavigationBarBottom ? getMenuHeight(menuHeight) : '0px',
          alignItems: { start: 'flex-start', center: 'center', end: 'flex-end' }[page.alignment ?? 'center'],
        }}
      >
        <GlobalAppLoading />

        {/* Top bar */}
        {showTopBar && <BuilderTopBarCore page={page} pageClient={pageClient} />}

        {/* Top navbar */}
        {showTopNavBar && <BuilderNavBar />}

        {/* Global Header */}
        {showGlobalHeader &&
          globalHeaderElements.map((el) => (
            <React.Fragment key={el.id}>
              {children({
                containerClient: pageClient,
                rowId: detailRowId,
                detailRowIndex,
                element: el,
                index: -1,
              })}
            </React.Fragment>
          ))}

        {page.elements.map((el, index) => {
          return (
            <React.Fragment key={el.id}>
              {children({
                containerClient: pageClient,
                rowId: detailRowId,
                detailRowIndex,
                element: el,
                index,
              })}
            </React.Fragment>
          )
        })}

        {/* Global Footer */}
        {showGlobalFooter && (
          <div className='mt-auto w-full'>
            {globalFooterElements.map((el) => (
              <React.Fragment key={el.id}>
                {children({
                  containerClient: pageClient,
                  rowId: detailRowId,
                  detailRowIndex,
                  element: el,
                  index: 999999,
                })}
              </React.Fragment>
            ))}
          </div>
        )}

        {showCookieConfirmation && <CookieConsentComponent />}

        {/* Bottom/Leading navbar */}
        {showBottomNavBar && <BuilderNavBar />}
      </div>
    )
  })
)
BuilderPageCore.displayName = 'BuilderPageCore'
